import React from 'react';
import { Box } from '@mui/material';
import GooeyNav from '../GooeyNav/GooeyNav';

/**
 * GooeyNavHeader - 与主页面一致的顶部导航栏（玻璃态 + GooeyNav）
 * 用于：主页、课程选择、以及所有带 sidebar 的页面
 * @param {Array} navItems - { label, onClick } 或 { label, path }
 * @param {ReactNode} leftSlot - 左侧插槽（如移动端抽屉按钮）
 * @param {string|ReactNode} centerTitle - 中间标题（如课程名）
 * @param {ReactNode} rightSlot - 右侧插槽（如语言切换、登出）
 * @param {ReactNode} trailingInBar - 玻璃条内右侧内容（如语言切换，与 GooeyNav 同条）
 * @param {number} initialActiveIndex - GooeyNav 初始高亮索引
 */
function GooeyNavHeader({
  navItems = [],
  leftSlot = null,
  centerTitle = null,
  rightSlot = null,
  trailingInBar = null,
  initialActiveIndex = 0,
  particleCount = 15,
  particleDistances = [90, 10],
  particleR = 100,
  animationTime = 600,
  timeVariance = 300,
  colors = [1, 2, 3, 1, 2, 3, 1, 4]
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        pt: 2,
        px: 2,
        pb: 1,
        minWidth: 0
      }}
    >
      {/* 左侧：始终 flex:1，使中间玻璃条整体居中；插槽内容在左侧区域 */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        {leftSlot}
      </Box>

      {/* 中间：玻璃态 GooeyNav，始终 content 宽度，左右各 flex:1 故整体居中 */}
      <Box
        sx={{
          flex: '0 0 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: 0
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px) saturate(180%)',
            WebkitBackdropFilter: 'blur(10px) saturate(180%)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            px: 2,
            py: 1,
            maxWidth: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflow: 'hidden'
          }}
        >
          <GooeyNav
            items={navItems}
            particleCount={particleCount}
            particleDistances={particleDistances}
            particleR={particleR}
            initialActiveIndex={initialActiveIndex}
            animationTime={animationTime}
            timeVariance={timeVariance}
            colors={colors}
          />
          {trailingInBar}
        </Box>
      </Box>

      {/* 中间标题（可选，如课程名；有 centerTitle 时占中间区域） */}
      {centerTitle ? (
        <Box
          sx={{
            flex: 1,
            display: { xs: 'none', sm: 'flex' },
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 0,
            px: 1
          }}
        >
          {typeof centerTitle === 'string' ? (
            <Box component="span" sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }} noWrap>
              {centerTitle}
            </Box>
          ) : (
            centerTitle
          )}
        </Box>
      ) : null}

      {/* 右侧：始终 flex:1，插槽内容在右侧区域 */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        {rightSlot}
      </Box>
    </Box>
  );
}

export default GooeyNavHeader;
